import { sql } from "drizzle-orm";
import { pgTable, text, varchar, integer, bigint, decimal, boolean, timestamp, date, jsonb, uuid, primaryKey, unique, index, uniqueIndex } from "drizzle-orm/pg-core";
import { relations } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Users table for admin authentication
export const users = pgTable("users", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull().unique(),
  password: text("password").notNull(),
  role: text("role", { enum: ["admin", "host", "portfolio_admin", "property_manager", "communications", "finance_readonly", "content_editor"] }).default("admin").notNull(),
  // Granular property access lives in user_property_access. `admin` is portfolio-wide.
  twofa_secret: text("twofa_secret"),
  created_at: timestamp("created_at").defaultNow().notNull(),
});

// Physical table name stays `property` (renaming it is unnecessary migration risk).
// New code should import `properties` (aliased below).
export const property = pgTable("property", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  title: text("title").notNull(),
  description: text("description").notNull(),
  address: text("address").notNull(),
  general_location: text("general_location"),
  lat: decimal("lat", { precision: 10, scale: 8 }),
  lng: decimal("lng", { precision: 11, scale: 8 }),
  check_in_time: text("check_in_time"),
  check_out_time: text("check_out_time"),
  max_guests: integer("max_guests"),
  bedrooms: integer("bedrooms"),
  bathrooms: integer("bathrooms"),
  rating: decimal("rating", { precision: 3, scale: 2 }),
  review_count: integer("review_count"),
  is_superhost: boolean("is_superhost").default(false),
  marketing_description: text("marketing_description"),
  
  // Access Information (for guest emails)
  gate_code: text("gate_code"),                    // e.g., "1203"
  door_code: text("door_code"),                    // e.g., "3535"
  wifi_network: text("wifi_network"),              // e.g., "The Kualoa Beach House"
  wifi_password: text("wifi_password"),            // e.g., "loveisthekey"
  
  // Directions & Parking
  driving_directions: text("driving_directions"),   // Detailed driving directions from airport
  parking_instructions: text("parking_instructions"), // e.g., "max 2 cars, park on premises"
  directions_url: text("directions_url"),           // Google Maps URL
  
  // House Manual & Rules
  house_manual: text("house_manual"),               // Full welcome/house manual text
  house_rules: text("house_rules"),                 // Detailed house rules
  quiet_hours_start: text("quiet_hours_start"),     // e.g., "8:00 PM"
  quiet_hours_end: text("quiet_hours_end"),         // e.g., "7:00 AM"
  
  // Checkout Instructions
  checkout_instructions: text("checkout_instructions"), // Checkout checklist
  
  // Emergency Contact
  emergency_contact_name: text("emergency_contact_name"),   // e.g., "Nina"
  emergency_contact_phone: text("emergency_contact_phone"), // e.g., "808-255-4348"

  // Security Deposit (admin-configurable; null or 0 = no deposit collected)
  security_deposit_amount: decimal("security_deposit_amount", { precision: 10, scale: 2 }),

  // "Regular" nightly rate shown struck through when a special/lower rate is live
  // (admin-set; null or <= the live rate hides the strikethrough).
  compare_at_rate: decimal("compare_at_rate", { precision: 10, scale: 2 }),

  slug: text("slug").unique(),
  publication_status: text("publication_status", { enum: ["draft", "published", "archived"] }).default("draft").notNull(),
  show_on_website: boolean("show_on_website").default(false).notNull(),
  booking_enabled: boolean("booking_enabled").default(false).notNull(),
  short_description: text("short_description"),
  hero_image_url: text("hero_image_url"),
  hero_video_url: text("hero_video_url"),
  timezone: text("timezone").default("Pacific/Honolulu").notNull(),
  currency: text("currency").default("USD").notNull(),
  display_order: integer("display_order").default(0).notNull(),
  is_featured: boolean("is_featured").default(false).notNull(),
  address_line: text("address_line"),
  city: text("city"),
  state: text("state"),
  postal_code: text("postal_code"),
  country: text("country").default("US"),
  public_location_label: text("public_location_label"),
  public_lat: decimal("public_lat", { precision: 10, scale: 8 }),
  public_lng: decimal("public_lng", { precision: 11, scale: 8 }),
  permit_number: text("permit_number"),
  tax_id: text("tax_id"),
  housekeeping_contact: text("housekeeping_contact"),
  maintenance_contact: text("maintenance_contact"),
  reply_to_email: text("reply_to_email"),
  updated_at: timestamp("updated_at").defaultNow().notNull(),

  created_at: timestamp("created_at").defaultNow().notNull(),
});

export const properties = property;

// Photos for the property
export const photos = pgTable("photos", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  property_id: uuid("property_id").references(() => property.id).notNull(),
  url: text("url").notNull(),
  alt: text("alt").notNull(),
  sort_order: integer("sort_order").default(0).notNull(),
  is_featured: boolean("is_featured").default(false),
  created_at: timestamp("created_at").defaultNow().notNull(),
});

// Nearby attractions for the property
export const nearby_attractions = pgTable("nearby_attractions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  property_id: uuid("property_id").references(() => property.id).notNull(),
  name: text("name").notNull(),
  description: text("description"),
  detailed_description: text("detailed_description"),
  distance: text("distance"),
  lat: decimal("lat", { precision: 10, scale: 8 }).notNull(),
  lng: decimal("lng", { precision: 11, scale: 8 }).notNull(),
  icon: text("icon").notNull(),
  color: text("color").notNull(),
  category: text("category", { enum: ["attraction", "restaurant", "beach", "entertainment", "shopping", "transportation", "cultural", "hike"] }).default("attraction").notNull(),
  image_url: text("image_url"),
  gallery_images: text("gallery_images").array(),
  website_url: text("website_url"),
  phone_number: text("phone_number"),
  address: text("address"),
  hours: text("hours"),
  rating: decimal("rating", { precision: 3, scale: 2 }),
  reviews_count: integer("reviews_count").default(0),
  tags: text("tags").array(),
  ticket_price: text("ticket_price"),
  sort_order: integer("sort_order").default(0).notNull(),
  active: boolean("active").default(true).notNull(),
  created_at: timestamp("created_at").defaultNow().notNull(),
});

// Amenity Categories
export const amenity_categories = pgTable("amenity_categories", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull().unique(),
  description: text("description"),
  icon: text("icon"),
  color: text("color"),
  sort_order: integer("sort_order").default(0).notNull(),
  created_at: timestamp("created_at").defaultNow().notNull(),
});

// Amenities
export const amenities = pgTable("amenities", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  icon: text("icon").notNull(),
  category: text("category").notNull(),
  description: text("description"),
  featured: boolean("featured").default(false),
  created_at: timestamp("created_at").defaultNow().notNull(),
});

// Property amenities junction table
export const property_amenities = pgTable("property_amenities", {
  property_id: uuid("property_id").references(() => property.id).notNull(),
  amenity_id: uuid("amenity_id").references(() => amenities.id).notNull(),
}, (table) => ({
  pk: primaryKey({ columns: [table.property_id, table.amenity_id] }),
}));

// Pricing rules for dynamic pricing
export const pricing_rules = pgTable("pricing_rules", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  property_id: uuid("property_id").references(() => property.id).notNull(),
  rule_type: text("rule_type", { enum: ["base", "seasonal", "weekend", "min_nights", "discount_long_stay", "cleaning_fee", "service_fee", "tat_rate", "get_rate", "county_tax_rate"] }).notNull(),
  start_date: date("start_date"),
  end_date: date("end_date"),
  value: decimal("value", { precision: 10, scale: 2 }).notNull(),
  min_nights: integer("min_nights"),
  percentage: boolean("percentage").default(false),
  active: boolean("active").default(true),
  created_at: timestamp("created_at").defaultNow().notNull(),
});

// Blackout dates
export const blackout_dates = pgTable("blackout_dates", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  property_id: uuid("property_id").references(() => property.id).notNull(),
  start_date: date("start_date").notNull(),
  end_date: date("end_date").notNull(),
  reason: text("reason").notNull(),
  created_at: timestamp("created_at").defaultNow().notNull(),
});

// Bookings
export const bookings = pgTable("bookings", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  property_id: uuid("property_id").references(() => property.id).notNull(),
  status: text("status", { enum: ["pending", "confirmed", "canceled", "declined"] }).default("pending").notNull(),
  start_date: date("start_date").notNull(),
  end_date: date("end_date").notNull(),
  nights: integer("nights").notNull(),
  guests: integer("guests").notNull(),
  subtotal: decimal("subtotal", { precision: 10, scale: 2 }).notNull(),
  taxes: decimal("taxes", { precision: 10, scale: 2 }).default("0").notNull(),
  fees: decimal("fees", { precision: 10, scale: 2 }).default("0").notNull(),
  total: decimal("total", { precision: 10, scale: 2 }).notNull(),
  currency: text("currency").default("USD").notNull(),
  payment_intent_id: text("payment_intent_id"),
  /**
   * Which configured Stripe account processed this booking.
   *
   * Stripe scopes payment intents, customers, payment methods and webhook secrets
   * per account, so every later operation for a booking (webhook verification,
   * intent retrieval, refunds, off-session balance capture) must run against the
   * same account the booking was created on. Pinned once, at first intent
   * creation, and never changed afterwards. Null on rows written before
   * multi-account support, which is read as "primary".
   *
   * Reporting-neutral: nothing reads this to filter, group, or exclude a booking.
   * It exists so the server knows which account to talk to.
   */
  stripe_account: text("stripe_account", { enum: ["primary", "secondary"] }),
  idempotency_key: text("idempotency_key").unique(),
  // New fields for manual booking support
  source: text("source", { enum: ["online", "phone", "airbnb", "vrbo", "manual"] }).default("online"),
  payment_status: text("payment_status", { enum: ["unpaid", "deposit_paid", "paid", "partially_refunded", "refunded", "disputed"] }).default("unpaid"),
  external_reference: text("external_reference"),
  admin_notes: text("admin_notes"),
  // Automated email tracking - prevents duplicate sends
  pre_arrival_email_sent: boolean("pre_arrival_email_sent").default(false),
  checkin_email_sent: boolean("checkin_email_sent").default(false),
  /** Admin marked the water waivers complete by hand (e.g. infants who cannot sign). */
  waivers_complete_override: boolean("waivers_complete_override").default(false),
  /** Admin marked the Guest Release complete by hand (e.g. infants who cannot sign). */
  release_complete_override: boolean("release_complete_override").default(false),
  checkout_email_sent: boolean("checkout_email_sent").default(false),
  post_stay_email_sent: boolean("post_stay_email_sent").default(false),
  // Security deposit (snapshotted at booking time from property config)
  security_deposit_amount: decimal("security_deposit_amount", { precision: 10, scale: 2 }),
  security_deposit_payment_intent_id: text("security_deposit_payment_intent_id"),
  security_deposit_status: text("security_deposit_status", { enum: ["not_collected", "held", "released", "kept"] }).default("not_collected"),
  // Split payment ("pay 50% now, balance before arrival"). Reduces the friction of a
  // large up-front total. Distinct from the refundable security_deposit_* fields above:
  // these track money actually collected toward the booking total.
  // payment_status moves unpaid -> deposit_paid -> paid.
  payment_plan: text("payment_plan", { enum: ["full", "split"] }).default("full"),
  amount_paid: decimal("amount_paid", { precision: 10, scale: 2 }).default("0"),
  balance_due: decimal("balance_due", { precision: 10, scale: 2 }).default("0"),
  balance_due_date: date("balance_due_date"),
  balance_payment_intent_id: text("balance_payment_intent_id"),
  balance_paid_at: timestamp("balance_paid_at"),
  balance_reminder_sent: boolean("balance_reminder_sent").default(false),
  // --- Deposit snapshot -------------------------------------------------
  // The deposit was previously only derivable by re-running computeSplitPlan against
  // the *current* total. That silently changes the historical deposit whenever a
  // booking is modified, so the amount actually charged is snapshotted here instead.
  deposit_amount: decimal("deposit_amount", { precision: 10, scale: 2 }),
  deposit_paid_at: timestamp("deposit_paid_at"),
  // --- Balance collection lifecycle -------------------------------------
  // scheduled  : balance owed, auto-charge armed for balance_due_date
  // manual     : owed, but no saved payment method — guest must pay via link
  // processing : a charge attempt is in flight (guards against double-charge)
  // paid       : settled
  // failed     : last attempt failed; see balance_failure_* and retry fields
  // canceled   : booking canceled/refunded — must never be auto-charged
  balance_status: text("balance_status", {
    enum: ["none", "scheduled", "manual", "processing", "paid", "failed", "canceled"],
  }).default("none"),
  balance_attempt_count: integer("balance_attempt_count").default(0).notNull(),
  balance_last_attempt_at: timestamp("balance_last_attempt_at"),
  balance_next_retry_at: timestamp("balance_next_retry_at"),
  balance_failure_code: text("balance_failure_code"),
  balance_failure_message: text("balance_failure_message"),
  // Set when a balance failure needs a human (retries exhausted / hard decline).
  balance_needs_attention: boolean("balance_needs_attention").default(false).notNull(),
  // --- Saved payment method (off-session balance capture) ---------------
  // Stripe references only — raw card data is never stored or transmitted here.
  stripe_customer_id: text("stripe_customer_id"),
  stripe_payment_method_id: text("stripe_payment_method_id"),
  // Display-only hints so admin/guest UI can say "Visa ending 4242" without a Stripe call.
  payment_method_brand: text("payment_method_brand"),
  payment_method_last4: text("payment_method_last4"),
  // --- Refunds ----------------------------------------------------------
  refunded_amount: decimal("refunded_amount", { precision: 10, scale: 2 }).default("0").notNull(),
  refunded_at: timestamp("refunded_at"),
  refund_reference: text("refund_reference"),
  // Per-booking unique door code (generated at confirmation; null = no code assigned yet)
  door_code: text("door_code"),
  // Readiness check email tracking (sent to admins 48h before check-in)
  readiness_check_sent: boolean("readiness_check_sent").default(false),
  created_at: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  // PERFORMANCE: Indexes on date columns for availability queries
  idx_start_date: index("idx_bookings_start_date").on(table.start_date),
  idx_end_date: index("idx_bookings_end_date").on(table.end_date),
  idx_date_range: index("idx_bookings_date_range").on(table.start_date, table.end_date),
  // PERFORMANCE: Index on property_id and status for filtering
  idx_property_status: index("idx_bookings_property_status").on(table.property_id, table.status),
  // RELIABILITY: PostgreSQL EXCLUDE constraint for preventing double bookings
  exclude_booking_overlap: sql`EXCLUDE USING gist (property_id WITH =, daterange(start_date, end_date, '[)') WITH &&) WHERE (status = 'confirmed')`,
}));

// Guests for bookings
export const guests = pgTable("guests", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  booking_id: uuid("booking_id").references(() => bookings.id).notNull(),
  first_name: text("first_name").notNull(),
  last_name: text("last_name").notNull(),
  email: text("email").notNull(),
  phone: text("phone"),
  is_primary: boolean("is_primary").default(false),
  created_at: timestamp("created_at").defaultNow().notNull(),
  // Identity verification fields
  id_verification_status: text("id_verification_status", {
    enum: ["not_submitted", "pending", "verified", "rejected"]
  }).default("not_submitted"),
  id_document_url: text("id_document_url"),
  id_verified_at: timestamp("id_verified_at"),
});

// Contact messages
export const messages = pgTable("messages", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  email: text("email").notNull(),
  subject: text("subject"),
  message: text("message").notNull(),
  ip: text("ip"),
  replied: boolean("replied").default(false),
  is_read: boolean("is_read").default(false).notNull(),
  status: text("status", {
    enum: ["open", "replied", "resolved", "archived", "spam", "deleted"],
  }).default("open").notNull(),
  category: text("category", {
    enum: ["conversation", "newsletter", "checkout", "system"],
  }).default("conversation").notNull(),
  property_id: uuid("property_id").references(() => property.id),
  /** Hidden from the inbox until this time; a reply from the guest clears it. */
  snoozed_until: timestamp("snoozed_until"),
  /** When the host was told this message had waited a day without an answer. */
  waiting_alert_sent_at: timestamp("waiting_alert_sent_at"),
  created_at: timestamp("created_at").defaultNow().notNull(),
});

// Chat messages for live chat feature
export const chat_messages = pgTable("chat_messages", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  email: text("email").notNull(),
  message: text("message").notNull(),
  is_admin: boolean("is_admin").default(false).notNull(),
  is_read: boolean("is_read").default(false).notNull(),
  session_status: text("session_status", {
    enum: ["open", "resolved", "archived", "spam", "deleted"],
  }).default("open").notNull(),
  session_id: text("session_id").notNull(),
  created_at: timestamp("created_at").defaultNow().notNull(),
});

// Email events for tracking
export const email_events = pgTable("email_events", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  booking_id: uuid("booking_id").references(() => bookings.id),
  template: text("template", { enum: ["booking_confirmation", "pre_arrival_reminder", "checkin_instructions", "checkout_instructions", "post_stay_followup", "admin_booking_alert", "admin_notification", "contact_reply", "guest_id_verified", "guest_id_rejected", "readiness_check", "balance_reminder", "abandoned_cart_followup", "waiver_request", "deposit_outcome", "weekly_prep_digest", "equipment_unlocked", "equipment_unlock_admin", "guest_decline", "admin_booking_status_change", "admin_payment_without_confirmation", "contact_auto_reply", "contact_form_alert", "chat_started_alert", "chat_escalation_alert", "chat_reply", "guest_message_waiting", "waiting_messages_digest", "discount_code", "review_thank_you", "stay_change_hold", "stay_change_confirmed", "stay_change_applied", "backlink_alert", "stay_access", "guest_release_request"] }).notNull(),
  recipient: text("recipient").notNull(),
  subject: text("subject").notNull(),
  sent_at: timestamp("sent_at").defaultNow().notNull(),
  provider_message_id: text("provider_message_id"),
  status: text("status", { enum: ["sent", "delivered", "failed", "bounced", "complained"] }).default("sent"),
  error_message: text("error_message"),
  opened_at: timestamp("opened_at"),
  clicked_at: timestamp("clicked_at"),
  scheduled_for: timestamp("scheduled_for"),
  property_id: uuid("property_id").references(() => property.id),
});

/**
 * Durable outbox for the guest booking-confirmation message.
 *
 * A booking can be confirmed by a webhook, a reconciliation job, or a manual
 * action.  The unique booking_id makes those paths converge on exactly one
 * normal confirmation request instead of treating email as an in-process
 * webhook side effect.  Provider acceptance is intentionally distinct from
 * delivered/opened provider events.
 */
export const booking_confirmation_deliveries = pgTable("booking_confirmation_deliveries", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  booking_id: uuid("booking_id").references(() => bookings.id, { onDelete: "cascade" }).notNull().unique(),
  status: text("status", {
    enum: [
      "pending",
      "processing",
      "retry_pending",
      "uncertain",
      "accepted",
      "delivered",
      "opened",
      "failed",
      "bounced",
      "complained",
      "cancelled",
    ],
  }).default("pending").notNull(),
  // Stable for a normal request. A deliberate resend gets a new, caller-supplied
  // request id rather than reusing the first request's provider idempotency key.
  provider_idempotency_key: text("provider_idempotency_key").notNull().unique(),
  manual_resend_request_id: text("manual_resend_request_id").unique(),
  attempt_count: integer("attempt_count").default(0).notNull(),
  next_attempt_at: timestamp("next_attempt_at").defaultNow().notNull(),
  first_attempt_at: timestamp("first_attempt_at"),
  // Reusing the same key after the provider's retention window can create a
  // second message. Uncertain requests stop for human reconciliation instead.
  // This begins at the first provider attempt, not when work is enqueued.
  idempotency_expires_at: timestamp("idempotency_expires_at"),
  // Immutable provider request snapshot (recipients, rendered template, and
  // attachments). Replaying a stable provider key must replay byte-for-byte
  // equivalent parameters even if a booking/template changes meanwhile.
  payload_json: jsonb("payload_json"),
  // An explicit resend has a new request key/payload, but retains the prior
  // uncertain/failed acceptance evidence for operator reconciliation.
  prior_delivery_evidence: jsonb("prior_delivery_evidence"),
  last_attempt_at: timestamp("last_attempt_at"),
  lease_token: uuid("lease_token"),
  lease_expires_at: timestamp("lease_expires_at"),
  provider_message_id: text("provider_message_id"),
  provider_accepted_at: timestamp("provider_accepted_at"),
  delivered_at: timestamp("delivered_at"),
  opened_at: timestamp("opened_at"),
  completed_at: timestamp("completed_at"),
  last_provider_status: text("last_provider_status"),
  last_error: text("last_error"),
  needs_attention: boolean("needs_attention").default(false).notNull(),
  created_at: timestamp("created_at").defaultNow().notNull(),
  updated_at: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  idx_status_next_attempt: index("idx_confirmation_delivery_status_next_attempt")
    .on(table.status, table.next_attempt_at),
  idx_provider_message: index("idx_confirmation_delivery_provider_message")
    .on(table.provider_message_id),
  idx_lease_expiry: index("idx_confirmation_delivery_lease_expiry")
    .on(table.lease_expires_at),
}));

/**
 * Verified provider events are retained independently of the outbox row's
 * provider_message_id assignment. A webhook can legitimately arrive between
 * the provider accepting mail and the worker committing that identifier.
 */
export const booking_confirmation_provider_events = pgTable("booking_confirmation_provider_events", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  provider_message_id: text("provider_message_id").notNull(),
  event_type: text("event_type", {
    enum: ["delivered", "opened", "bounced", "complained", "failed"],
  }).notNull(),
  // Provider webhook id when supplied; otherwise a deterministic local
  // de-duplication key based on message id + event type is used.
  provider_event_id: text("provider_event_id"),
  dedupe_key: text("dedupe_key").notNull().unique(),
  occurred_at: timestamp("occurred_at"),
  created_at: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  idx_provider_message_order: index("idx_confirmation_provider_event_message_order")
    .on(table.provider_message_id, table.occurred_at, table.created_at),
}));

// Email templates for customization
export const email_templates = pgTable("email_templates", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull().unique(),
  subject: text("subject").notNull(),
  html_content: text("html_content").notNull(),
  text_content: text("text_content"),
  /** The hidden line email clients show after the subject. */
  preview_text: text("preview_text"),
  is_active: boolean("is_active").default(true),
  /**
   * False while the row still holds the catalog default, which the server
   * refreshes on every boot so code improvements reach it. True once an
   * admin saves an edit; the row is then theirs until they reset it.
   */
  is_customized: boolean("is_customized").default(false).notNull(),
  variables: jsonb("variables").default('[]'),
  created_at: timestamp("created_at").defaultNow().notNull(),
  updated_at: timestamp("updated_at").defaultNow().notNull(),
});

// Email threads for conversation grouping
export const email_threads = pgTable("email_threads", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  guest_email: text("guest_email").notNull(),
  guest_name: text("guest_name"),
  subject: text("subject").notNull(),
  booking_id: uuid("booking_id").references(() => bookings.id),
  property_id: uuid("property_id").references(() => property.id),
  status: text("status", {
    enum: ["open", "replied", "resolved", "archived", "spam", "deleted"],
  }).default("open").notNull(),
  thread_type: text("thread_type", { enum: ["booking", "inquiry", "support", "review_request", "notification"] }).default("booking").notNull(),
  last_message_at: timestamp("last_message_at").defaultNow().notNull(),
  unread_count: integer("unread_count").default(0).notNull(),
  /** Hidden from the inbox until this time; a reply from the guest clears it. */
  snoozed_until: timestamp("snoozed_until"),
  /** When the host was told this thread had waited a day without an answer. */
  waiting_alert_sent_at: timestamp("waiting_alert_sent_at"),
  created_at: timestamp("created_at").defaultNow().notNull(),
  updated_at: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  idx_guest_email: index("idx_email_threads_guest_email").on(table.guest_email),
  idx_booking_id: index("idx_email_threads_booking_id").on(table.booking_id),
  idx_status: index("idx_email_threads_status").on(table.status),
  idx_last_message: index("idx_email_threads_last_message").on(table.last_message_at),
}));

// Email messages for storing individual emails in threads
export const email_messages = pgTable("email_messages", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  thread_id: uuid("thread_id").references(() => email_threads.id).notNull(),
  direction: text("direction", { enum: ["outgoing", "incoming", "system"] }).notNull(),
  from_email: text("from_email").notNull(),
  from_name: text("from_name"),
  to_email: text("to_email").notNull(),
  reply_to: text("reply_to"),
  cc: text("cc"),
  subject: text("subject").notNull(),
  html_content: text("html_content"),
  text_content: text("text_content"),
  template_name: text("template_name"),
  provider_message_id: text("provider_message_id"),
  status: text("status", { enum: ["queued", "sent", "delivered", "failed", "bounced", "opened", "clicked"] }).default("sent").notNull(),
  error_message: text("error_message"),
  metadata: jsonb("metadata"),
  is_read: boolean("is_read").default(false).notNull(),
  sent_at: timestamp("sent_at").defaultNow().notNull(),
  created_at: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  idx_thread_id: index("idx_email_messages_thread_id").on(table.thread_id),
  idx_direction: index("idx_email_messages_direction").on(table.direction),
  idx_sent_at: index("idx_email_messages_sent_at").on(table.sent_at),
  // Provider IDs are globally unique. This makes at-least-once webhook
  // delivery idempotent even when duplicate requests arrive concurrently.
  uniq_provider_message_id: uniqueIndex("email_messages_provider_message_id_unique")
    .on(table.provider_message_id)
    .where(sql`${table.provider_message_id} IS NOT NULL`),
}));

// Audit logs for admin actions
export const audit_logs = pgTable("audit_logs", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  user_id: uuid("user_id").references(() => users.id),
  action: text("action").notNull(),
  entity_type: text("entity_type").notNull(),
  entity_id: text("entity_id"),
  old_values: jsonb("old_values"),
  new_values: jsonb("new_values"),
  ip: text("ip"),
  created_at: timestamp("created_at").defaultNow().notNull(),
});

// Source migration must be applied before arrival-policy code is released.
// Existing bookings deliberately have no claim: unchanged arrivals are grandfathered.
export const arrival_claims = pgTable("arrival_claims", {
  entity_key: text("entity_key").primaryKey(),
  override_user_id: uuid("override_user_id").references(() => users.id),
  override_reason: text("override_reason"),
  booking_id: uuid("booking_id").references(() => bookings.id, { onDelete: "cascade" }).notNull(),
  property_id: uuid("property_id").references(() => property.id).notNull(),
  start_date: date("start_date").notNull(),
  expires_at: timestamp("expires_at", { withTimezone: true }),
  payment_started: boolean("payment_started").default(false).notNull(),
  released_at: timestamp("released_at", { withTimezone: true }),
  created_at: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [index("arrival_claims_active_date").on(table.start_date, table.property_id).where(sql`${table.released_at} IS NULL`)]);

// Coupons
export const coupons = pgTable("coupons", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  code: text("code").notNull().unique(),
  type: text("type", { enum: ["percent", "fixed"] }).notNull(),
  value: decimal("value", { precision: 10, scale: 2 }).notNull(),
  start_date: date("start_date"),
  end_date: date("end_date"),
  min_nights: integer("min_nights"),
  usage_limit: integer("usage_limit"),
  used_count: integer("used_count").default(0),
  allow_stacking: boolean("allow_stacking").default(false).notNull(),
  active: boolean("active").default(true),
  created_at: timestamp("created_at").defaultNow().notNull(),
  scope: text("scope", { enum: ["portfolio", "selected_properties"] }).default("portfolio").notNull(),
});

// Site promotions: pricing rules + public marketing cards (admin-editable)
export const promotions = pgTable("promotions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  type: text("type", {
    enum: ["last_minute_rate", "min_nights_schedule", "display"],
  }).notNull(),
  title: text("title").notNull(),
  terms: text("terms").notNull(),
  config: jsonb("config").default(sql`'{}'::jsonb`).notNull(),
  active: boolean("active").default(true).notNull(),
  show_on_site: boolean("show_on_site").default(true).notNull(),
  sort_order: integer("sort_order").default(0).notNull(),
  starts_on: date("starts_on"),
  ends_on: date("ends_on"),
  created_at: timestamp("created_at").defaultNow().notNull(),
  updated_at: timestamp("updated_at").defaultNow().notNull(),
  scope: text("scope", { enum: ["portfolio", "selected_properties"] }).default("portfolio").notNull(),
});

// Email subscribers / marketing leads (captured via the booking-page email offer)
export const subscribers = pgTable("subscribers", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull().unique(),
  coupon_code: text("coupon_code"),
  source: text("source").default("booking_popup"),
  status: text("status", { enum: ["active", "unsubscribed"] }).default("active"),
  created_at: timestamp("created_at").defaultNow().notNull(),
});

// Abandoned checkout leads (email captured during booking; follow-up emailed after 2 days)
export const checkout_leads = pgTable("checkout_leads", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull(),
  first_name: text("first_name"),
  last_name: text("last_name"),
  check_in: date("check_in"),
  check_out: date("check_out"),
  guests: integer("guests"),
  coupon_code: text("coupon_code"),
  booking_id: uuid("booking_id").references(() => bookings.id),
  payment_intent_id: text("payment_intent_id"),
  status: text("status", {
    enum: ["active", "payment_pending", "converted", "canceled", "sending", "delivered", "sent"],
  }).default("active").notNull(),
  last_activity_at: timestamp("last_activity_at").defaultNow().notNull(),
  followup_claimed_at: timestamp("followup_claimed_at"),
  followup_provider_message_id: text("followup_provider_message_id"),
  cancellation_reason: text("cancellation_reason"),
  followup_sent: boolean("followup_sent").default(false).notNull(),
  followup_sent_at: timestamp("followup_sent_at"),
  property_id: uuid("property_id").references(() => property.id),
  created_at: timestamp("created_at").defaultNow().notNull(),
  updated_at: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  idx_checkout_leads_followup: index("idx_checkout_leads_followup").on(table.followup_sent, table.status, table.last_activity_at),
  idx_checkout_leads_email: index("idx_checkout_leads_email").on(table.email),
  idx_checkout_leads_booking: index("idx_checkout_leads_booking").on(table.booking_id),
  idx_checkout_leads_property: index("idx_checkout_leads_property").on(table.property_id),
}));

// ─── Financial Traceability Tables ───────────────────────────────

/**
 * Immutable pricing snapshot captured at booking creation time.
 * All amounts are integer cents. Never recalculate from current rules.
 */
export const pricing_snapshots = pgTable("pricing_snapshots", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  // Nullable FK: preserved even if the booking is deleted.
  booking_id: uuid("booking_id").references(() => bookings.id, { onDelete: "set null" }),
  // Stable reference that survives booking deletion.
  booking_reference: text("booking_reference").notNull(),
  // Snapshot of booking dates + party
  check_in: date("check_in").notNull(),
  check_out: date("check_out").notNull(),
  nights: integer("nights").notNull(),
  guests: integer("guests").notNull(),
  currency: text("currency").default("USD").notNull(),
  // Integer-cent amounts (authoritative, never recalculated)
  subtotal_cents: integer("subtotal_cents").notNull(),
  fees_cents: integer("fees_cents").notNull().default(0),
  discount_cents: integer("discount_cents").notNull().default(0),
  discount_components: jsonb("discount_components").default([]).notNull(),
  tax_cents: integer("tax_cents").notNull().default(0),
  total_cents: integer("total_cents").notNull(),
  // Per-night breakdown (JSON array of {label, nights, rate_cents, amount_cents})
  nightly_breakdown: jsonb("nightly_breakdown").default(sql`'[]'::jsonb`),
  // Fee components (JSON array of {label, amount_cents})
  fee_components: jsonb("fee_components").default(sql`'[]'::jsonb`),
  // Tax components (JSON array of {label, rate, amount_cents})
  tax_components: jsonb("tax_components").default(sql`'[]'::jsonb`),
  // Coupon / promotion details
  coupon_code: text("coupon_code"),
  promotion_id: text("promotion_id"),
  promotion_type: text("promotion_type"),
  // Monotonic version per booking_reference. A new snapshot never overwrites an
  // older one; instead it is written with the next version. Queries return the
  // latest version. This is distinct from formula_version below.
  snapshot_version: integer("snapshot_version").notNull().default(1),
  // Calculation formula version (semantic, e.g. "1.0", "legacy", "manual").
  formula_version: text("formula_version").default("1.0").notNull(),
  source: text("source", { enum: ["online", "manual", "backfill"] }).default("online").notNull(),
  property_id: uuid("property_id").references(() => property.id),
  property_name: text("property_name"),
  tax_jurisdiction: text("tax_jurisdiction"),
  cancellation_policy_version: text("cancellation_policy_version"),
  created_at: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  idx_booking_id: index("idx_pricing_snapshots_booking_id").on(table.booking_id),
  idx_pricing_snapshots_property: index("idx_pricing_snapshots_property").on(table.property_id),
  idx_booking_reference: index("idx_pricing_snapshots_booking_ref").on(table.booking_reference),
  idx_created_at: index("idx_pricing_snapshots_created_at").on(table.created_at),
  // A given reference can hold multiple immutable versions; each version is unique.
  uq_reference_version: uniqueIndex("uq_pricing_snapshots_ref_version").on(
    table.booking_reference,
    table.snapshot_version,
  ),
}));

/**
 * Ad attribution captured when a booking is created, so the Stripe webhook can
 * send a server-side Purchase to Meta that matches the browser pixel.
 *
 * Declared here even though every read and write in server/meta-capi.ts is raw
 * SQL. The deploy generates its migration by diffing THIS file against the
 * production database, so a table that exists in production and not in this file
 * is read as one you deleted: the deploy proposed `DROP TABLE
 * "booking_attribution" CASCADE` against production, and would have proposed it
 * again on every publish until the declaration existed. The SQL migration that
 * creates the table is not enough on its own.
 */
export const booking_attribution = pgTable("booking_attribution", {
  booking_id: uuid("booking_id")
    .primaryKey()
    .references(() => bookings.id, { onDelete: "cascade" }),
  fbp: text("fbp"),
  fbc: text("fbc"),
  client_ip: text("client_ip"),
  user_agent: text("user_agent"),
  event_source_url: text("event_source_url"),
  meta_purchase_sent_at: timestamp("meta_purchase_sent_at"),
  meta_purchase_response: jsonb("meta_purchase_response"),
  created_at: timestamp("created_at").defaultNow().notNull(),
});

export type BookingAttribution = typeof booking_attribution.$inferSelect;

/**
 * Immutable financial transaction log.
 * Signed integer cents: positive = money in, negative = money out (refunds).
 * Stable idempotency_key prevents duplicate inserts.
 */
export const financial_transactions = pgTable("financial_transactions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  // Idempotency: unique across the table — safe to re-insert on retry
  idempotency_key: text("idempotency_key").notNull().unique(),
  // Nullable FK: preserved even if the booking is deleted.
  booking_id: uuid("booking_id").references(() => bookings.id, { onDelete: "set null" }),
  booking_reference: text("booking_reference"),
  // Transaction type
  type: text("type", {
    enum: [
      "payment",           // Main booking charge (full or deposit)
      "balance_payment",   // Balance leg of a split booking
      "security_deposit",  // Refundable security deposit hold
      "security_release",  // Security deposit refund
      "security_kept",     // Security deposit kept (no refund)
      "refund",            // Full or partial cancellation refund (money out)
      "credit",            // Goodwill / account credit issued to a guest (money out)
      "discount",          // Post-booking discount applied (money out, reduces amount owed)
      "adjustment",        // Manual correction (signed)
    ],
  }).notNull(),
  // Signed cents: positive = collected, negative = refunded/released
  amount_cents: integer("amount_cents").notNull(),
  currency: text("currency").default("USD").notNull(),
  // Lifecycle status
  status: text("status", {
    enum: ["pending", "succeeded", "failed", "canceled"],
  }).default("succeeded").notNull(),
  // Stripe references (safe display only — no raw card data)
  /** Stripe account the provider ids below belong to; null is read as "primary". */
  stripe_account: text("stripe_account", { enum: ["primary", "secondary"] }),
  stripe_payment_intent_id: text("stripe_payment_intent_id"),
  stripe_charge_id: text("stripe_charge_id"),
  stripe_refund_id: text("stripe_refund_id"),
  // Payment method (display only)
  payment_method_brand: text("payment_method_brand"),
  payment_method_last4: text("payment_method_last4"),
  // Actor / audit trail
  actor: text("actor").default("system").notNull(),
  reason: text("reason"),
  metadata: jsonb("metadata").default(sql`'{}'::jsonb`),
  property_id: uuid("property_id").references(() => property.id),
  created_at: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  idx_booking_id: index("idx_fin_tx_booking_id").on(table.booking_id),
  idx_fin_tx_property: index("idx_fin_tx_property").on(table.property_id),
  idx_booking_reference: index("idx_fin_tx_booking_ref").on(table.booking_reference),
  idx_type: index("idx_fin_tx_type").on(table.type),
  idx_created_at: index("idx_fin_tx_created_at").on(table.created_at),
  idx_stripe_pi: index("idx_fin_tx_stripe_pi").on(table.stripe_payment_intent_id),
  // Prevent applying the same Stripe charge or refund twice. A single PaymentIntent
  // can legitimately spawn different transaction types (e.g. payment then refund),
  // so provider-id uniqueness is scoped per (provider id, type).
  uq_stripe_charge_type: uniqueIndex("uq_fin_tx_stripe_charge_type")
    .on(table.stripe_charge_id, table.type)
    .where(sql`${table.stripe_charge_id} IS NOT NULL`),
  uq_stripe_refund: uniqueIndex("uq_fin_tx_stripe_refund")
    .on(table.stripe_refund_id)
    .where(sql`${table.stripe_refund_id} IS NOT NULL`),
}));

/**
 * Durable, immutable refund command binding. The allocation is frozen before
 * the first provider call so retries cannot move money to newly collected legs.
 */
export const booking_refund_requests = pgTable("booking_refund_requests", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  booking_id: uuid("booking_id").notNull().references(() => bookings.id, { onDelete: "restrict" }),
  request_id: text("request_id").notNull(),
  amount_cents: integer("amount_cents").notNull(),
  currency: text("currency").notNull(),
  reason: text("reason").notNull(),
  actor_admin_id: text("actor_admin_id"),
  allocations: jsonb("allocations").notNull(),
  provider_refunds: jsonb("provider_refunds").default(sql`'{}'::jsonb`).notNull(),
  status: text("status", {
    enum: ["planned", "pending", "completed", "reconciliation_required"],
  }).default("planned").notNull(),
  created_at: timestamp("created_at").defaultNow().notNull(),
  updated_at: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  uq_booking_refund_request: uniqueIndex("uq_booking_refund_request")
    .on(table.booking_id, table.request_id),
  uq_booking_unresolved_refund: uniqueIndex("uq_booking_unresolved_refund")
    .on(table.booking_id)
    .where(sql`${table.status} IN ('planned', 'pending', 'reconciliation_required')`),
}));

// Type exports for financial traceability tables
export type PricingSnapshot = typeof pricing_snapshots.$inferSelect;
export type InsertPricingSnapshot = typeof pricing_snapshots.$inferInsert;
export type FinancialTransaction = typeof financial_transactions.$inferSelect;
export type InsertFinancialTransaction = typeof financial_transactions.$inferInsert;
export type BookingRefundRequest = typeof booking_refund_requests.$inferSelect;

// Settings for global configuration
export const settings = pgTable("settings", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  key: text("key").unique().notNull(),
  value: jsonb("value").notNull(),
  updated_at: timestamp("updated_at").defaultNow().notNull(),
});

// Guest reviews
export const guest_reviews = pgTable("guest_reviews", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  property_id: uuid("property_id").references(() => property.id).notNull(),
  booking_id: uuid("booking_id").references(() => bookings.id),
  guest_name: text("guest_name").notNull(),
  guest_email: text("guest_email").notNull(),
  location: text("location"),
  rating: integer("rating").notNull(),
  review_date: date("review_date").notNull(),
  stay_start_date: date("stay_start_date").notNull(),
  stay_end_date: date("stay_end_date").notNull(),
  trip_type: text("trip_type", { enum: ["Solo", "Couple", "Family", "Group", "Business"] }),
  review_text: text("review_text"),
  would_recommend: boolean("would_recommend").default(true),
  is_featured: boolean("is_featured").default(false),
  approval_status: text("approval_status", { enum: ["pending", "approved", "rejected"] }).default("pending").notNull(),
  verified_guest: boolean("verified_guest").default(false),
  admin_notes: text("admin_notes"),
  response_from_host: text("response_from_host"),
  response_date: timestamp("response_date"),
  ip_address: text("ip_address"),
  submitted_at: timestamp("submitted_at").defaultNow().notNull(),
  approved_at: timestamp("approved_at"),
  approved_by: uuid("approved_by").references(() => users.id),
  created_at: timestamp("created_at").defaultNow().notNull(),
});

// Airbnb reviews for external review integration
export const airbnb_reviews = pgTable("airbnb_reviews", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  property_id: uuid("property_id").references(() => property.id).notNull(),
  airbnb_review_id: text("airbnb_review_id").notNull().unique(),
  reviewer_name: text("reviewer_name").notNull(),
  reviewer_location: text("reviewer_location"),
  rating: integer("rating").notNull(),
  review_text: text("review_text"),
  review_date: date("review_date").notNull(),
  verified_stay: boolean("verified_stay").default(true),
  response_from_host: text("response_from_host"),
  reviewer_avatar_url: text("reviewer_avatar_url"),
  source: text("source").default("airbnb").notNull(),
  sync_date: timestamp("sync_date").defaultNow().notNull(),
  is_featured: boolean("is_featured").default(false),
  active: boolean("active").default(true),
  created_at: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  // PERFORMANCE: Index on property_id for filtering
  idx_property_id: index("idx_airbnb_reviews_property_id").on(table.property_id),
  // PERFORMANCE: Index on sync_date for sync management
  idx_sync_date: index("idx_airbnb_reviews_sync_date").on(table.sync_date),
  // PERFORMANCE: Index on review_date for ordering
  idx_review_date: index("idx_airbnb_reviews_review_date").on(table.review_date),
  // PERFORMANCE: Compound index for unified review filtering and ordering
  idx_property_active_review_date: index("idx_airbnb_reviews_property_active_review_date").on(table.property_id, table.active, table.review_date),
}));

// External calendars for Airbnb/Vrbo integration
export const external_calendars = pgTable("external_calendars", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  property_id: uuid("property_id").references(() => property.id).notNull(),
  platform: text("platform", { enum: ["airbnb", "vrbo", "booking", "homeaway", "other"] }).notNull(),
  name: text("name").notNull(),
  ical_url: text("ical_url").notNull(),
  active: boolean("active").default(true).notNull(),
  sync_enabled: boolean("sync_enabled").default(true).notNull(),
  bookings_only: boolean("bookings_only").default(true).notNull(), // Only sync actual bookings, not availability blocks
  last_sync_at: timestamp("last_sync_at"),
  next_sync_at: timestamp("next_sync_at"),
  sync_frequency: integer("sync_frequency").default(300).notNull(), // seconds, default 5 minutes
  etag: text("etag"), // for conditional requests
  last_modified: text("last_modified"), // for conditional requests
  sync_errors: integer("sync_errors").default(0).notNull(),
  last_error: text("last_error"),
  created_at: timestamp("created_at").defaultNow().notNull(),
  updated_at: timestamp("updated_at").defaultNow().notNull(),
});

// External reservations imported from iCal feeds
export const external_reservations = pgTable("external_reservations", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  calendar_id: uuid("calendar_id").references(() => external_calendars.id).notNull(),
  external_uid: text("external_uid").notNull(), // unique ID from external calendar
  platform: text("platform", { enum: ["airbnb", "vrbo", "booking", "homeaway", "other"] }).notNull(),
  start_date: date("start_date").notNull(),
  end_date: date("end_date").notNull(),
  status: text("status", { enum: ["reserved", "blocked", "tentative", "cancelled"] }).default("reserved").notNull(),
  guest_name: text("guest_name"),
  title: text("title"), // event title from iCal
  description: text("description"),
  is_blocking: boolean("is_blocking").default(true).notNull(), // whether this blocks availability
  raw_event: jsonb("raw_event"), // store original iCal event data
  // Enhanced fields for better context on Airbnb blocks
  block_type: text("block_type", { enum: ["reservation", "manual_block", "prep_time", "buffer", "unknown"] }).default("unknown"),
  confirmation_code: text("confirmation_code"), // Airbnb confirmation code (e.g., HMABCDEF)
  guest_email: text("guest_email"),
  guest_phone: text("guest_phone"),
  num_guests: integer("num_guests"),
  created_at: timestamp("created_at").defaultNow().notNull(),
  updated_at: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  // SECURITY: Unique constraint prevents duplicate external reservations
  unique_calendar_external_uid: unique("unique_calendar_external_uid").on(table.calendar_id, table.external_uid),
  // PERFORMANCE: Indexes on date columns for fast availability queries
  idx_start_date: index("idx_external_reservations_start_date").on(table.start_date),
  idx_end_date: index("idx_external_reservations_end_date").on(table.end_date),
  idx_date_range: index("idx_external_reservations_date_range").on(table.start_date, table.end_date),
  // PERFORMANCE: Index on calendar_id for filtering by calendar
  idx_calendar_id: index("idx_external_reservations_calendar_id").on(table.calendar_id),
  // RELIABILITY: PostgreSQL EXCLUDE constraint for date range conflicts
  exclude_date_overlap: sql`EXCLUDE USING gist (calendar_id WITH =, daterange(start_date, end_date, '[)') WITH &&) WHERE (is_blocking = true AND status IN ('reserved', 'blocked'))`,
}));

// Temporary holds during checkout process
export const holds = pgTable("holds", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  property_id: uuid("property_id").references(() => property.id).notNull(),
  start_date: date("start_date").notNull(),
  end_date: date("end_date").notNull(),
  reason: text("reason", { enum: ["checkout", "admin_block", "maintenance"] }).default("checkout").notNull(),
  reference_id: text("reference_id"), // session ID, booking ID, etc.
  release_token: text("release_token").unique(),
  expires_at: timestamp("expires_at").notNull(),
  created_at: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  // PERFORMANCE: Indexes on date columns for hold management
  idx_start_date: index("idx_holds_start_date").on(table.start_date),
  idx_end_date: index("idx_holds_end_date").on(table.end_date),
  idx_expires_at: index("idx_holds_expires_at").on(table.expires_at),
  // PERFORMANCE: Index on property_id for filtering
  idx_property_id: index("idx_holds_property_id").on(table.property_id),
  // RELIABILITY: PostgreSQL EXCLUDE constraint for preventing overlapping holds on same dates
  exclude_hold_overlap: sql`EXCLUDE USING gist (property_id WITH =, daterange(start_date, end_date, '[)') WITH &&) WHERE (expires_at > now())`,
}));

// Stripe webhook event ledger.
//
// Stripe guarantees at-least-once delivery, so the same event id can arrive more
// than once (retries after a timeout, network replays, or a manual resend from the
// dashboard). Booking state alone is not a safe dedup key: two concurrent copies of
// the same event can both read the pre-update state and both proceed, which is how
// duplicate confirmation emails and duplicate door codes happen.
//
// The event id is the primary key, so `INSERT ... ON CONFLICT DO NOTHING` is an
// atomic claim: exactly one delivery wins the insert and does the work. A delivery
// that loses the race returns immediately.
//
// A row is only left behind in `completed`. If handling throws, the row is deleted
// so Stripe's next retry can legitimately re-run the handler.
export const webhook_events = pgTable("webhook_events", {
  // Stripe's event id (evt_...). Primary key == the dedup guarantee.
  id: text("id").primaryKey(),
  type: text("type").notNull(),
  booking_id: uuid("booking_id").references(() => bookings.id),
  status: text("status", { enum: ["processing", "completed", "failed"] })
    .default("processing")
    .notNull(),
  // Last error message when status = 'failed', for admin diagnosis.
  error_message: text("error_message"),
  attempts: integer("attempts").default(1).notNull(),
  received_at: timestamp("received_at").defaultNow().notNull(),
  completed_at: timestamp("completed_at"),
}, (table) => ({
  idx_type: index("idx_webhook_events_type").on(table.type),
  idx_booking: index("idx_webhook_events_booking").on(table.booking_id),
  idx_status: index("idx_webhook_events_status").on(table.status),
}));

// Sync run logs for monitoring and debugging
export const sync_runs = pgTable("sync_runs", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  calendar_id: uuid("calendar_id").references(() => external_calendars.id).notNull(),
  started_at: timestamp("started_at").defaultNow().notNull(),
  completed_at: timestamp("completed_at"),
  status: text("status", { enum: ["running", "success", "error", "timeout"] }).default("running").notNull(),
  reservations_imported: integer("reservations_imported").default(0).notNull(),
  reservations_updated: integer("reservations_updated").default(0).notNull(),
  reservations_deleted: integer("reservations_deleted").default(0).notNull(),
  http_status: integer("http_status"),
  response_time_ms: integer("response_time_ms"),
  error_message: text("error_message"),
  etag_used: text("etag_used"),
  last_modified_used: text("last_modified_used"),
  created_at: timestamp("created_at").defaultNow().notNull(),
});

// Review solicitations for automated post-stay review collection
export const review_solicitations = pgTable("review_solicitations", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  booking_id: uuid("booking_id").references(() => bookings.id).notNull(),
  guest_email: text("guest_email").notNull(),
  guest_name: text("guest_name").notNull(),
  checkout_date: date("checkout_date").notNull(),
  solicitation_sent_date: timestamp("solicitation_sent_date"),
  review_submitted: boolean("review_submitted").default(false).notNull(),
  email_status: text("email_status", { enum: ["pending", "sent", "delivered", "failed", "bounced"] }).default("pending").notNull(),
  review_link_token: text("review_link_token").notNull().unique(),
  token_expires_at: timestamp("token_expires_at").notNull(),
  retry_count: integer("retry_count").default(0).notNull(),
  last_error: text("last_error"),
  email_provider_id: text("email_provider_id"), // Resend message ID
  created_at: timestamp("created_at").defaultNow().notNull(),
  updated_at: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  // PERFORMANCE: Index on booking_id for looking up solicitations by booking
  idx_booking_id: index("idx_review_solicitations_booking_id").on(table.booking_id),
  // PERFORMANCE: Index on checkout_date for finding eligible bookings
  idx_checkout_date: index("idx_review_solicitations_checkout_date").on(table.checkout_date),
  // PERFORMANCE: Index on email_status for filtering pending/failed solicitations
  idx_email_status: index("idx_review_solicitations_email_status").on(table.email_status),
  // PERFORMANCE: Index on token_expires_at for cleanup of expired tokens
  idx_token_expires_at: index("idx_review_solicitations_token_expires_at").on(table.token_expires_at),
  // UNIQUENESS: One solicitation per booking to prevent duplicates
  unique_booking_solicitation: unique("unique_booking_solicitation").on(table.booking_id),
}));

// Relations
export const propertyRelations = relations(property, ({ many }) => ({
  photos: many(photos),
  amenities: many(property_amenities),
  pricing_rules: many(pricing_rules),
  blackout_dates: many(blackout_dates),
  bookings: many(bookings),
  guest_reviews: many(guest_reviews),
  airbnb_reviews: many(airbnb_reviews),
  external_calendars: many(external_calendars),
  holds: many(holds),
}));

export const photosRelations = relations(photos, ({ one }) => ({
  property: one(property, {
    fields: [photos.property_id],
    references: [property.id],
  }),
}));

export const amenitiesRelations = relations(amenities, ({ many }) => ({
  properties: many(property_amenities),
}));

export const propertyAmenitiesRelations = relations(property_amenities, ({ one }) => ({
  property: one(property, {
    fields: [property_amenities.property_id],
    references: [property.id],
  }),
  amenity: one(amenities, {
    fields: [property_amenities.amenity_id],
    references: [amenities.id],
  }),
}));

export const bookingsRelations = relations(bookings, ({ one, many }) => ({
  property: one(property, {
    fields: [bookings.property_id],
    references: [property.id],
  }),
  guests: many(guests),
  email_events: many(email_events),
}));

export const guestsRelations = relations(guests, ({ one }) => ({
  booking: one(bookings, {
    fields: [guests.booking_id],
    references: [bookings.id],
  }),
}));

export const guestReviewsRelations = relations(guest_reviews, ({ one }) => ({
  property: one(property, {
    fields: [guest_reviews.property_id],
    references: [property.id],
  }),
  booking: one(bookings, {
    fields: [guest_reviews.booking_id],
    references: [bookings.id],
  }),
  approvedBy: one(users, {
    fields: [guest_reviews.approved_by],
    references: [users.id],
  }),
}));

export const airbnbReviewsRelations = relations(airbnb_reviews, ({ one }) => ({
  property: one(property, {
    fields: [airbnb_reviews.property_id],
    references: [property.id],
  }),
}));

export const externalCalendarsRelations = relations(external_calendars, ({ one, many }) => ({
  property: one(property, {
    fields: [external_calendars.property_id],
    references: [property.id],
  }),
  reservations: many(external_reservations),
  sync_runs: many(sync_runs),
}));

export const externalReservationsRelations = relations(external_reservations, ({ one }) => ({
  calendar: one(external_calendars, {
    fields: [external_reservations.calendar_id],
    references: [external_calendars.id],
  }),
}));

export const holdsRelations = relations(holds, ({ one }) => ({
  property: one(property, {
    fields: [holds.property_id],
    references: [property.id],
  }),
}));

export const syncRunsRelations = relations(sync_runs, ({ one }) => ({
  calendar: one(external_calendars, {
    fields: [sync_runs.calendar_id],
    references: [external_calendars.id],
  }),
}));

export const reviewSolicitationsRelations = relations(review_solicitations, ({ one }) => ({
  booking: one(bookings, {
    fields: [review_solicitations.booking_id],
    references: [bookings.id],
  }),
}));

export const nearbyAttractionsRelations = relations(nearby_attractions, ({ one }) => ({
  property: one(property, {
    fields: [nearby_attractions.property_id],
    references: [property.id],
  }),
}));

// Zod schemas for validation
export const insertUserSchema = createInsertSchema(users).pick({
  email: true,
  password: true,
  role: true,
});

export const insertPropertySchema = createInsertSchema(property);

export const insertPhotoSchema = createInsertSchema(photos).omit({
  id: true,
  created_at: true,
});

export const insertNearbyAttractionSchema = createInsertSchema(nearby_attractions).omit({
  id: true,
  created_at: true,
});

export const insertAmenityCategorySchema = createInsertSchema(amenity_categories).omit({
  id: true,
  created_at: true,
});

export const insertAmenitySchema = createInsertSchema(amenities).omit({
  id: true,
  created_at: true,
});

export const insertPricingRuleSchema = createInsertSchema(pricing_rules).omit({
  id: true,
  created_at: true,
});

export const insertBlackoutDateSchema = createInsertSchema(blackout_dates).omit({
  id: true,
  created_at: true,
});

export const insertBookingSchema = createInsertSchema(bookings).omit({
  id: true,
  created_at: true,
});

export const insertGuestSchema = createInsertSchema(guests).omit({
  id: true,
  created_at: true,
});

export const insertMessageSchema = createInsertSchema(messages).omit({
  id: true,
  created_at: true,
  replied: true,
});

export const insertChatMessageSchema = createInsertSchema(chat_messages).omit({
  id: true,
  created_at: true,
});

export const insertCouponSchema = createInsertSchema(coupons).omit({
  id: true,
  created_at: true,
  used_count: true,
});

export const insertPromotionSchema = createInsertSchema(promotions).omit({
  id: true,
  created_at: true,
  updated_at: true,
});

export const insertSubscriberSchema = createInsertSchema(subscribers).omit({
  id: true,
  created_at: true,
});

export const insertCheckoutLeadSchema = createInsertSchema(checkout_leads).omit({
  id: true,
  created_at: true,
  followup_sent: true,
  followup_sent_at: true,
  coupon_code: true,
});

export const insertGuestReviewSchema = createInsertSchema(guest_reviews).omit({
  id: true,
  property_id: true,
  created_at: true,
  submitted_at: true,
  approved_at: true,
  approval_status: true,
  verified_guest: true,
});

export const insertAirbnbReviewSchema = createInsertSchema(airbnb_reviews).omit({
  id: true,
  created_at: true,
  sync_date: true,
});

export const insertEmailTemplateSchema = createInsertSchema(email_templates).omit({
  id: true,
  created_at: true,
  updated_at: true,
});

export const insertEmailThreadSchema = createInsertSchema(email_threads).omit({
  id: true,
  created_at: true,
  updated_at: true,
});

export const insertEmailMessageSchema = createInsertSchema(email_messages).omit({
  id: true,
  created_at: true,
});

export const insertExternalCalendarSchema = createInsertSchema(external_calendars).omit({
  id: true,
  created_at: true,
  updated_at: true,
  last_sync_at: true,
  next_sync_at: true,
  etag: true,
  last_modified: true,
  sync_errors: true,
  last_error: true,
});

export const insertExternalReservationSchema = createInsertSchema(external_reservations).omit({
  id: true,
  created_at: true,
  updated_at: true,
});

export const insertHoldSchema = createInsertSchema(holds).omit({
  id: true,
  created_at: true,
});

export const insertSyncRunSchema = createInsertSchema(sync_runs).omit({
  id: true,
  created_at: true,
  completed_at: true,
  reservations_imported: true,
  reservations_updated: true,
  reservations_deleted: true,
  http_status: true,
  response_time_ms: true,
  error_message: true,
  etag_used: true,
  last_modified_used: true,
});

export const insertReviewSolicitationSchema = createInsertSchema(review_solicitations).omit({
  id: true,
  created_at: true,
  updated_at: true,
});

// Types
export type User = typeof users.$inferSelect;
export type InsertUser = z.infer<typeof insertUserSchema>;
export type Property = typeof property.$inferSelect;
export type InsertProperty = z.infer<typeof insertPropertySchema>;
export type Photo = typeof photos.$inferSelect;
export type InsertPhoto = z.infer<typeof insertPhotoSchema>;
export type NearbyAttraction = typeof nearby_attractions.$inferSelect;
export type InsertNearbyAttraction = z.infer<typeof insertNearbyAttractionSchema>;
export type AmenityCategory = typeof amenity_categories.$inferSelect;
export type InsertAmenityCategory = z.infer<typeof insertAmenityCategorySchema>;
export type Amenity = typeof amenities.$inferSelect;
export type InsertAmenity = z.infer<typeof insertAmenitySchema>;
export type PricingRule = typeof pricing_rules.$inferSelect;
export type InsertPricingRule = z.infer<typeof insertPricingRuleSchema>;
export type BlackoutDate = typeof blackout_dates.$inferSelect;
export type InsertBlackoutDate = z.infer<typeof insertBlackoutDateSchema>;
export type Booking = typeof bookings.$inferSelect;
export type InsertBooking = z.infer<typeof insertBookingSchema>;
export type Guest = typeof guests.$inferSelect;
export type InsertGuest = z.infer<typeof insertGuestSchema>;
export type Message = typeof messages.$inferSelect;
export type InsertMessage = z.infer<typeof insertMessageSchema>;
export type ChatMessage = typeof chat_messages.$inferSelect;
export type InsertChatMessage = z.infer<typeof insertChatMessageSchema>;
export type EmailEvent = typeof email_events.$inferSelect;
export type BookingConfirmationDelivery = typeof booking_confirmation_deliveries.$inferSelect;
export type AuditLog = typeof audit_logs.$inferSelect;
export type Coupon = typeof coupons.$inferSelect;
export type InsertCoupon = z.infer<typeof insertCouponSchema>;
export type Promotion = typeof promotions.$inferSelect;
export type InsertPromotion = z.infer<typeof insertPromotionSchema>;
export type Subscriber = typeof subscribers.$inferSelect;
export type InsertSubscriber = z.infer<typeof insertSubscriberSchema>;
export type CheckoutLead = typeof checkout_leads.$inferSelect;
export type InsertCheckoutLead = z.infer<typeof insertCheckoutLeadSchema>;
export type Setting = typeof settings.$inferSelect;
export type GuestReview = typeof guest_reviews.$inferSelect;
export type InsertGuestReview = z.infer<typeof insertGuestReviewSchema>;
export type AirbnbReview = typeof airbnb_reviews.$inferSelect;
export type InsertAirbnbReview = z.infer<typeof insertAirbnbReviewSchema>;
export type EmailTemplate = typeof email_templates.$inferSelect;
export type InsertEmailTemplate = z.infer<typeof insertEmailTemplateSchema>;
export type EmailThread = typeof email_threads.$inferSelect;
export type InsertEmailThread = z.infer<typeof insertEmailThreadSchema>;
export type EmailMessage = typeof email_messages.$inferSelect;
export type InsertEmailMessage = z.infer<typeof insertEmailMessageSchema>;
export type ExternalCalendar = typeof external_calendars.$inferSelect;
export type InsertExternalCalendar = z.infer<typeof insertExternalCalendarSchema>;
export type ExternalReservation = typeof external_reservations.$inferSelect;
export type InsertExternalReservation = z.infer<typeof insertExternalReservationSchema>;
export type Hold = typeof holds.$inferSelect;
export type InsertHold = z.infer<typeof insertHoldSchema>;
export type SyncRun = typeof sync_runs.$inferSelect;
export type InsertSyncRun = z.infer<typeof insertSyncRunSchema>;
export type ReviewSolicitation = typeof review_solicitations.$inferSelect;
export type InsertReviewSolicitation = z.infer<typeof insertReviewSolicitationSchema>;

// Page Builder Tables for drag-and-drop content management
export const page_layouts = pgTable("page_layouts", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(), // e.g., "Home Page", "Stay Page"
  slug: text("slug").notNull(),
  description: text("description"),
  property_id: uuid("property_id").references(() => property.id),
  page_type: text("page_type", { enum: ["global", "property"] }).default("global").notNull(),
  is_active: boolean("is_active").default(true).notNull(),
  created_at: timestamp("created_at").defaultNow().notNull(),
  updated_at: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  uqLayoutSlug: uniqueIndex("uq_page_layouts_property_slug").on(table.property_id, table.slug),
}));

export const content_blocks = pgTable("content_blocks", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  layout_id: uuid("layout_id").references(() => page_layouts.id).notNull(),
  block_type: text("block_type").notNull(), // "hero", "gallery", "amenities", "reviews", etc.
  title: text("title"),
  content: jsonb("content"), // Flexible content storage
  settings: jsonb("settings"), // Block-specific settings
  sort_order: integer("sort_order").default(0).notNull(),
  is_visible: boolean("is_visible").default(true).notNull(),
  created_at: timestamp("created_at").defaultNow().notNull(),
  updated_at: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  layoutOrderIdx: index("content_blocks_layout_order").on(table.layout_id, table.sort_order),
}));

export const block_types = pgTable("block_types", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull().unique(), // "hero", "gallery", "amenities"
  label: text("label").notNull(), // "Hero Section", "Photo Gallery"
  description: text("description"),
  icon: text("icon").notNull(), // Lucide icon name
  category: text("category").notNull(), // "content", "media", "layout"
  default_settings: jsonb("default_settings"),
  is_active: boolean("is_active").default(true).notNull(),
  created_at: timestamp("created_at").defaultNow().notNull(),
});

// Zod schemas for page builder
export const insertPageLayoutSchema = createInsertSchema(page_layouts);
export const insertContentBlockSchema = createInsertSchema(content_blocks);
export const insertBlockTypeSchema = createInsertSchema(block_types);

export type PageLayout = typeof page_layouts.$inferSelect;
export type ContentBlock = typeof content_blocks.$inferSelect;
export type BlockType = typeof block_types.$inferSelect;
export type InsertPageLayout = z.infer<typeof insertPageLayoutSchema>;
export type InsertContentBlock = z.infer<typeof insertContentBlockSchema>;
export type InsertBlockType = z.infer<typeof insertBlockTypeSchema>;

// Unified Review Interface for combining guest and Airbnb reviews
export interface UnifiedReview {
  id: string;
  guest_name: string;
  location?: string | null;
  rating: number;
  review_text?: string | null;
  review_date: string;
  source: 'guest' | 'airbnb';
  verified_stay: boolean;
  avatar_url?: string | null;
  is_featured: boolean;
  would_recommend?: boolean;
  trip_type?: string | null;
  response_from_host?: string | null;
  // Additional metadata
  original_id: string;
  created_at: string;
}

// Public property type that excludes private fields and includes additional data
export type PublicProperty = Omit<Property,
  | "address"
  | "address_line"
  | "lat"
  | "lng"
  | "gate_code"
  | "door_code"
  | "wifi_network"
  | "wifi_password"
  | "driving_directions"
  | "parking_instructions"
  | "directions_url"
  | "house_manual"
  | "checkout_instructions"
  | "emergency_contact_name"
  | "emergency_contact_phone"
  | "housekeeping_contact"
  | "maintenance_contact"
  | "permit_number"
  | "tax_id"
  | "city"
  | "state"
  | "postal_code"
  | "country"
> & {
  general_location: string;
  slug: string | null;
  publication_status?: string;
  show_on_website?: boolean;
  booking_enabled?: boolean;
  photos?: Photo[];
  amenities?: Amenity[];
  base_rate?: string;
  regular_rate?: string | null;
  compare_at_rate?: string | null;
  minNights?: number;
};

// ========== BLOG SYSTEM ==========

// Blog Categories
export const blog_categories = pgTable("blog_categories", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull().unique(),
  slug: text("slug").notNull().unique(),
  description: text("description"),
  icon: text("icon"),
  color: text("color"),
  sort_order: integer("sort_order").default(0).notNull(),
  created_at: timestamp("created_at").defaultNow().notNull(),
});

// Blog Posts
export const blog_posts = pgTable("blog_posts", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  title: text("title").notNull(),
  slug: text("slug").notNull().unique(),
  excerpt: text("excerpt").notNull(),
  content: text("content").notNull(),
  hero_image_url: text("hero_image_url"),
  hero_image_alt: text("hero_image_alt"),
  category_id: uuid("category_id").references(() => blog_categories.id).notNull(),
  related_post_ids: text("related_post_ids").array(),
  meta_title: text("meta_title"),
  meta_description: text("meta_description"),
  word_count: integer("word_count").default(0),
  reading_time_minutes: integer("reading_time_minutes").default(0),
  status: text("status", { enum: ["draft", "published", "archived"] }).default("draft").notNull(),
  published_at: timestamp("published_at"),
  featured: boolean("featured").default(false),
  sort_order: integer("sort_order").default(0).notNull(),
  created_at: timestamp("created_at").defaultNow().notNull(),
  updated_at: timestamp("updated_at").defaultNow().notNull(),
});

// Blog relations
export const blogCategoriesRelations = relations(blog_categories, ({ many }) => ({
  posts: many(blog_posts),
}));

export const blogPostsRelations = relations(blog_posts, ({ one }) => ({
  category: one(blog_categories, {
    fields: [blog_posts.category_id],
    references: [blog_categories.id],
  }),
}));

// Blog Zod schemas
export const insertBlogCategorySchema = createInsertSchema(blog_categories).omit({ id: true, created_at: true });
export const insertBlogPostSchema = createInsertSchema(blog_posts).omit({ id: true, created_at: true, updated_at: true });

// Blog Types
export type BlogCategory = typeof blog_categories.$inferSelect;
export type BlogPost = typeof blog_posts.$inferSelect;
export type InsertBlogCategory = z.infer<typeof insertBlogCategorySchema>;
export type InsertBlogPost = z.infer<typeof insertBlogPostSchema>;

// Blog post with category included
export type BlogPostWithCategory = BlogPost & {
  category: BlogCategory;
};

// ========== END BLOG SYSTEM ==========

// ========== BACKLINK HEALTH MONITOR ==========

// Referring domains tracked from Ahrefs
export const referring_domains = pgTable("referring_domains", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  domain: text("domain").notNull().unique(),
  domain_rating: decimal("domain_rating", { precision: 5, scale: 1 }),
  is_spam: boolean("is_spam").default(false),
  links_to_target: integer("links_to_target").default(0),
  traffic_domain: integer("traffic_domain").default(0),
  first_seen: date("first_seen"),
  status: text("status", { enum: ["legit", "toxic", "pending"] }).default("pending").notNull(),
  is_disavowed: boolean("is_disavowed").default(false).notNull(),
  detected_at: timestamp("detected_at").defaultNow().notNull(),
  last_checked: timestamp("last_checked").defaultNow().notNull(),
  last_seen: timestamp("last_seen"),
});

// Domain rating / backlink count history over time
export const authority_history = pgTable("authority_history", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  date: date("date").notNull(),
  domain_rating: decimal("domain_rating", { precision: 5, scale: 1 }),
  referring_domains_count: integer("referring_domains_count").default(0),
  backlinks_count: integer("backlinks_count").default(0),
  created_at: timestamp("created_at").defaultNow().notNull(),
});

// Alerts for backlink events
export const backlink_alerts = pgTable("backlink_alerts", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  type: text("type", { enum: ["new_toxic", "lost_link", "new_legit"] }).notNull(),
  domain: text("domain").notNull(),
  message: text("message").notNull(),
  detected_at: timestamp("detected_at").defaultNow().notNull(),
  acknowledged: boolean("acknowledged").default(false).notNull(),
});

export const insertReferringDomainSchema = createInsertSchema(referring_domains).omit({ id: true, detected_at: true, last_checked: true });
export const insertAuthorityHistorySchema = createInsertSchema(authority_history).omit({ id: true, created_at: true });
export const insertBacklinkAlertSchema = createInsertSchema(backlink_alerts).omit({ id: true, detected_at: true });

export type ReferringDomain = typeof referring_domains.$inferSelect;
export type InsertReferringDomain = z.infer<typeof insertReferringDomainSchema>;
export type AuthorityHistory = typeof authority_history.$inferSelect;
export type InsertAuthorityHistory = z.infer<typeof insertAuthorityHistorySchema>;
export type BacklinkAlert = typeof backlink_alerts.$inferSelect;
export type InsertBacklinkAlert = z.infer<typeof insertBacklinkAlertSchema>;

// ========== END BACKLINK HEALTH MONITOR ==========

// ========== LINK OPPORTUNITIES ENGINE ==========

// Seed lists: competitors + directories/associations/citations used for discovery
export const link_targets = pgTable("link_targets", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  type: text("type", { enum: ["competitor", "directory", "association", "citation"] }).notNull(),
  domain: text("domain").notNull(),
  name: text("name"),
  notes: text("notes"),
  active: boolean("active").default(true).notNull(),
  created_at: timestamp("created_at").defaultNow().notNull(),
});

// Discovered, scored backlink opportunities
export const link_opportunities = pgTable("link_opportunities", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  domain: text("domain").notNull(),
  name: text("name"),
  play_type: text("play_type", {
    enum: [
      "link_intersect", "broken_link", "resource_page", "directory", "citation",
      "association", "unlinked_mention", "guest_post", "partnership", "digital_pr",
    ],
  }).notNull(),
  source: text("source"),
  domain_rating: decimal("domain_rating", { precision: 5, scale: 1 }),
  traffic: bigint("traffic", { mode: "number" }).default(0),
  relevance_score: integer("relevance_score").default(0).notNull(),
  spam_flag: boolean("spam_flag").default(false).notNull(),
  priority_score: integer("priority_score").default(0).notNull(),
  status: text("status", { enum: ["new", "qualified", "ready_to_send", "contacted", "replied", "won", "lost", "rejected"] }).default("new").notNull(),
  contact_url: text("contact_url"),
  contact_email: text("contact_email"),
  needs_manual_contact: boolean("needs_manual_contact").default(false).notNull(),
  reject_reason: text("reject_reason"),
  last_verified_at: timestamp("last_verified_at"),
  notes: text("notes"),
  discovered_at: timestamp("discovered_at").defaultNow().notNull(),
  updated_at: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  domainPlayTypeUnique: unique("link_opportunities_domain_play_type_unique").on(table.domain, table.play_type),
}));

export const insertLinkTargetSchema = createInsertSchema(link_targets).omit({ id: true, created_at: true });
export const insertLinkOpportunitySchema = createInsertSchema(link_opportunities).omit({ id: true, discovered_at: true, updated_at: true });

export type LinkTarget = typeof link_targets.$inferSelect;
export type InsertLinkTarget = z.infer<typeof insertLinkTargetSchema>;
export type LinkOpportunity = typeof link_opportunities.$inferSelect;
export type InsertLinkOpportunity = z.infer<typeof insertLinkOpportunitySchema>;

// ========== END LINK OPPORTUNITIES ENGINE ==========

// ========== OUTREACH ENGINE (Phase 2) ==========

// Admin-editable blocklist honored by discovery + refinement
export const blocked_domains = pgTable("blocked_domains", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  domain: text("domain").notNull().unique(),
  reason: text("reason"),
  created_at: timestamp("created_at").defaultNow().notNull(),
});

// Outreach drafts + send audit log (one row per message/touch)
export const outreach_messages = pgTable("outreach_messages", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  opportunity_id: uuid("opportunity_id").notNull().references(() => link_opportunities.id, { onDelete: "cascade" }),
  touch_number: integer("touch_number").default(1).notNull(), // 1 = initial, 2 = follow-up (hard max 2)
  subject: text("subject").notNull(),
  body: text("body").notNull(),
  to_email: text("to_email"),
  status: text("status", { enum: ["draft", "queued", "sent", "failed", "canceled"] }).default("draft").notNull(),
  provider: text("provider"),
  provider_message_id: text("provider_message_id"),
  unsubscribe_token: text("unsubscribe_token").notNull(),
  error_message: text("error_message"),
  sent_at: timestamp("sent_at"),
  created_at: timestamp("created_at").defaultNow().notNull(),
  updated_at: timestamp("updated_at").defaultNow().notNull(),
});

// Suppression / opt-out list (unsubscribes + manual entries)
export const outreach_suppressions = pgTable("outreach_suppressions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull().unique(),
  reason: text("reason", { enum: ["unsubscribed", "manual", "bounced", "complained"] }).default("unsubscribed").notNull(),
  created_at: timestamp("created_at").defaultNow().notNull(),
});

// Manual citations/directories checklist (never auto-submitted)
export const citation_checklist = pgTable("citation_checklist", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull().unique(),
  submission_url: text("submission_url").notNull(),
  notes: text("notes"),
  status: text("status", { enum: ["todo", "submitted", "verified"] }).default("todo").notNull(),
  submitted_at: timestamp("submitted_at"),
  verified_at: timestamp("verified_at"),
  created_at: timestamp("created_at").defaultNow().notNull(),
});

export const insertBlockedDomainSchema = createInsertSchema(blocked_domains).omit({ id: true, created_at: true });
export const insertOutreachMessageSchema = createInsertSchema(outreach_messages).omit({ id: true, created_at: true, updated_at: true });
export const insertOutreachSuppressionSchema = createInsertSchema(outreach_suppressions).omit({ id: true, created_at: true });
export const insertCitationChecklistSchema = createInsertSchema(citation_checklist).omit({ id: true, created_at: true });

export type BlockedDomain = typeof blocked_domains.$inferSelect;
export type InsertBlockedDomain = z.infer<typeof insertBlockedDomainSchema>;
export type OutreachMessage = typeof outreach_messages.$inferSelect;
export type InsertOutreachMessage = z.infer<typeof insertOutreachMessageSchema>;
export type OutreachSuppression = typeof outreach_suppressions.$inferSelect;
export type InsertOutreachSuppression = z.infer<typeof insertOutreachSuppressionSchema>;
export type CitationChecklistItem = typeof citation_checklist.$inferSelect;
export type InsertCitationChecklistItem = z.infer<typeof insertCitationChecklistSchema>;

// ========== END OUTREACH ENGINE ==========

// Date validation schemas for preventing SQL errors and improving UX
export const dateStringSchema = z.string()
  .min(1, "Date is required")
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format")
  .refine((date) => {
    const parsed = new Date(date + 'T00:00:00.000Z');
    return !isNaN(parsed.getTime());
  }, "Invalid date value")
  .refine((date) => {
    const parsed = new Date(date + 'T00:00:00.000Z');
    const year = parsed.getFullYear();
    return year >= 1900 && year <= 2100;
  }, "Date must be between 1900 and 2100");

export const futureDateSchema = dateStringSchema
  .refine((date) => {
    const parsed = new Date(date + 'T00:00:00.000Z');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return parsed >= today;
  }, "Date cannot be in the past");

export const dateRangeSchema = z.object({
  start_date: dateStringSchema,
  end_date: dateStringSchema,
}).refine((data) => {
  const startDate = new Date(data.start_date + 'T00:00:00.000Z');
  const endDate = new Date(data.end_date + 'T00:00:00.000Z');
  return endDate > startDate;
}, {
  message: "End date must be after start date",
  path: ["end_date"]
});

export const bookingDateRangeSchema = z.object({
  start_date: futureDateSchema,
  end_date: dateStringSchema,
}).refine((data) => {
  const startDate = new Date(data.start_date + 'T00:00:00.000Z');
  const endDate = new Date(data.end_date + 'T00:00:00.000Z');
  return endDate > startDate;
}, {
  message: "Check-out date must be after check-in date",
  path: ["end_date"]
}).refine((data) => {
  const startDate = new Date(data.start_date + 'T00:00:00.000Z');
  const endDate = new Date(data.end_date + 'T00:00:00.000Z');
  const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays <= 365; // Maximum 1 year booking
}, {
  message: "Booking duration cannot exceed 365 days",
  path: ["end_date"]
});

// Quote request validation schema
export const quoteRequestSchema = z.object({
  start: dateStringSchema,
  end: dateStringSchema,
  guests: z.string()
    .regex(/^\d+$/, "Guests must be a number")
    .transform(val => parseInt(val, 10))
    .refine(val => val >= 1, "At least 1 guest is required")
    .refine(val => val <= 20, "Guest count exceeds the supported maximum"),
  coupon: z.string().optional(),
  property: z.string().optional(),
  propertyId: z.string().uuid().optional(),
}).refine((data) => {
  const startDate = new Date(data.start + 'T00:00:00.000Z');
  const endDate = new Date(data.end + 'T00:00:00.000Z');
  return endDate > startDate;
}, {
  message: "Check-out date must be after check-in date",
  path: ["end"]
});

// Availability request validation schema
export const availabilityRequestSchema = z.object({
  start: dateStringSchema,
  end: dateStringSchema
}).refine((data) => {
  const startDate = new Date(data.start + 'T00:00:00.000Z');
  const endDate = new Date(data.end + 'T00:00:00.000Z');
  return endDate > startDate;
}, {
  message: "End date must be after start date",
  path: ["end"]
});

// Helper functions for date validation and safe conversion
export const dateValidationHelpers = {
  isValidDateString: (dateStr: string): boolean => {
    try {
      dateStringSchema.parse(dateStr);
      return true;
    } catch {
      return false;
    }
  },

  safeParseDate: (dateStr: string): Date | null => {
    try {
      dateStringSchema.parse(dateStr);
      return new Date(dateStr + 'T00:00:00.000Z');
    } catch {
      return null;
    }
  },

  isValidDateRange: (startStr: string, endStr: string): boolean => {
    try {
      dateRangeSchema.parse({ start_date: startStr, end_date: endStr });
      return true;
    } catch {
      return false;
    }
  },

  createValidationError: (message: string, field?: string) => ({
    error: 'VALIDATION_ERROR',
    message,
    field,
    timestamp: new Date().toISOString()
  })
};

export type QuoteRequest = z.infer<typeof quoteRequestSchema>;
export type AvailabilityRequest = z.infer<typeof availabilityRequestSchema>;
export type DateRange = z.infer<typeof dateRangeSchema>;
export type BookingDateRange = z.infer<typeof bookingDateRangeSchema>;

// ═══════════════════════════════════════════════════════════════════════════════
// Legal documents: versioned House Rules, liability terms and waivers, plus the
// append-only record of what each guest accepted.
//
// Published versions and acceptance rows are protected by database triggers, not
// just by convention — see scripts/db-migrations/sql/20260901120000_legal_documents.sql.
// Editing today's House Rules must never alter what a guest accepted last month.
// ═══════════════════════════════════════════════════════════════════════════════

export const legal_documents = pgTable("legal_documents", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  /** Stable identifier used in code: house_rules, liability_terms, water_activity_waiver, guest_release.
   *  NOT globally unique: a slug has one global row plus at most one override per
   *  property. Uniqueness is enforced by two partial indexes in
   *  20260901120000_legal_documents.sql. Marking it .unique() here makes drizzle
   *  push create legal_documents_slug_unique, which forbids overrides outright. */
  slug: text("slug").notNull(),
  title: text("title").notNull(),
  description: text("description"),
  /** Whether a direct booking cannot complete until this is accepted. */
  required_for_booking: boolean("required_for_booking").default(false).notNull(),
  is_active: boolean("is_active").default(true).notNull(),
  sort_order: integer("sort_order").default(0).notNull(),
  /** NULL = the global default that applies to every property. A value scopes
   *  this row to one property, overriding the global document of the same slug. */
  property_id: uuid("property_id").references(() => property.id, { onDelete: "cascade" }),
  created_at: timestamp("created_at").defaultNow().notNull(),
  updated_at: timestamp("updated_at").defaultNow().notNull(),
});

export const legal_document_versions = pgTable("legal_document_versions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  document_id: uuid("document_id").notNull().references(() => legal_documents.id, { onDelete: "cascade" }),
  version_number: integer("version_number").notNull(),
  status: text("status", { enum: ["draft", "published", "archived"] }).default("draft").notNull(),
  /**
   * The flattened text exactly as shown to the guest. Stored rather than
   * recomputed from sections, so a later change to rendering logic cannot
   * retroactively alter what we claim someone agreed to.
   */
  rendered_content: text("rendered_content"),
  /** Hash of rendered_content: identifies the version and detects real changes. */
  content_hash: text("content_hash"),
  change_summary: text("change_summary"),
  /** True when guests with existing reservations must accept again. */
  is_material_change: boolean("is_material_change").default(false).notNull(),
  effective_from: timestamp("effective_from"),
  published_at: timestamp("published_at"),
  published_by: uuid("published_by").references(() => users.id, { onDelete: "set null" }),
  created_by: uuid("created_by").references(() => users.id, { onDelete: "set null" }),
  created_at: timestamp("created_at").defaultNow().notNull(),
  updated_at: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  uqVersion: uniqueIndex("uq_legal_version_number").on(table.document_id, table.version_number),
  idxStatus: index("idx_legal_version_status").on(table.document_id, table.status),
  idxHash: index("idx_legal_version_hash").on(table.content_hash),
}));

export const legal_document_sections = pgTable("legal_document_sections", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  version_id: uuid("version_id").notNull().references(() => legal_document_versions.id, { onDelete: "cascade" }),
  heading: text("heading").notNull(),
  body: text("body").notNull(),
  anchor: text("anchor"),
  sort_order: integer("sort_order").default(0).notNull(),
  is_enabled: boolean("is_enabled").default(true).notNull(),
  created_at: timestamp("created_at").defaultNow().notNull(),
  updated_at: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  idxVersion: index("idx_legal_sections_version").on(table.version_id, table.sort_order),
}));

export const legal_acceptances = pgTable("legal_acceptances", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  document_id: uuid("document_id").notNull().references(() => legal_documents.id, { onDelete: "restrict" }),
  version_id: uuid("version_id").notNull().references(() => legal_document_versions.id, { onDelete: "restrict" }),
  booking_id: uuid("booking_id").references(() => bookings.id, { onDelete: "set null" }),
  booking_reference: text("booking_reference"),
  guest_id: uuid("guest_id").references(() => guests.id, { onDelete: "set null" }),
  /** Denormalised so the record stands alone if the guest row is later changed. */
  guest_email: text("guest_email").notNull(),
  guest_name: text("guest_name"),
  accepted_at: timestamp("accepted_at").defaultNow().notNull(),
  acceptance_method: text("acceptance_method", {
    enum: ["booking_checkout", "guest_portal", "emailed_link", "admin_recorded"],
  }).default("booking_checkout").notNull(),
  ip_address: text("ip_address"),
  user_agent: text("user_agent"),
  signature_name: text("signature_name"),
  /** The guest's confirmation that they will pass the rules to their whole party. */
  party_confirmed: boolean("party_confirmed").default(false).notNull(),
  accepted_content_hash: text("accepted_content_hash"),
  /** Full text of exactly what this signer saw. HRS ch. 489E treats an
   *  electronic record as satisfying retention only if it accurately reflects
   *  the information and stays accessible, so the signature row carries its own
   *  copy rather than depending on the version row alone. */
  accepted_content: text("accepted_content"),
  /** HRS s 663-1.54(b)(1): the inherent risks were fully disclosed first. */
  risk_disclosure_ack: boolean("risk_disclosure_ack").default(false).notNull(),
  /** HRS s 663-1.54(b)(2): this patron confirmed they are physically able. */
  physically_able_ack: boolean("physically_able_ack").default(false).notNull(),
  /** HRS s 663-1.54(b)(2): this patron was given the safety instruction. */
  instruction_ack: boolean("instruction_ack").default(false).notNull(),
  /** "booker" accepts for the reservation; "adult_participant" signs the waiver
   *  for themselves; "adult_signer" signs the Guest Release for themselves;
   *  "guardian" acknowledges supervision of a minor WITHOUT waiving the minor's
   *  claims, which Hawaii would not enforce anyway. */
  signer_role: text("signer_role", {
    enum: ["booker", "adult_participant", "adult_signer", "guardian"],
  }).default("booker").notNull(),
  /** Which property's terms this signature was given under.
   *
   *  Declared here because it exists in the database: the per-property legal
   *  migration adds and backfills it, and `server/legal-acceptance.ts` writes it
   *  on every acceptance. Leaving it out of this table made `drizzle-kit push`
   *  generate `ALTER TABLE legal_acceptances DROP COLUMN property_id`, which
   *  destroys signed legal evidence and breaks the accept endpoint until the
   *  next boot repairs it. It is not derivable from the document alone: a
   *  global default document has a NULL property_id while the booking is for a
   *  specific home, which is why the signature carries its own copy. */
  property_id: uuid("property_id").references(() => property.id),
  metadata: jsonb("metadata").default(sql`'{}'::jsonb`).notNull(),
  created_at: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  idxBooking: index("idx_legal_acceptances_booking").on(table.booking_id),
  idxRole: index("idx_legal_acceptances_role").on(table.signer_role, table.accepted_at),
  idxDocument: index("idx_legal_acceptances_document").on(table.document_id, table.accepted_at),
  idxEmail: index("idx_legal_acceptances_email").on(table.guest_email),
  idxVersion: index("idx_legal_acceptances_version").on(table.version_id),
  idxProperty: index("idx_legal_acceptances_property").on(table.property_id, table.accepted_at),
  uqSigner: uniqueIndex("uq_legal_acceptance_booking_version_signer")
    .on(table.booking_id, table.version_id, sql`lower(${table.guest_email})`)
    .where(sql`${table.booking_id} IS NOT NULL`),
}));

export type LegalDocument = typeof legal_documents.$inferSelect;
export type LegalDocumentVersion = typeof legal_document_versions.$inferSelect;
export type LegalDocumentSection = typeof legal_document_sections.$inferSelect;
export type LegalAcceptance = typeof legal_acceptances.$inferSelect;
export type InsertLegalAcceptance = typeof legal_acceptances.$inferInsert;

export const user_property_access = pgTable("user_property_access", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  user_id: uuid("user_id").references(() => users.id, { onDelete: "cascade" }).notNull(),
  property_id: uuid("property_id").references(() => property.id, { onDelete: "restrict" }).notNull(),
  role: text("role", {
    enum: ["portfolio_admin", "property_manager", "communications", "finance_readonly", "content_editor"],
  }).notNull(),
  created_at: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  uqUserProperty: uniqueIndex("uq_user_property_access").on(table.user_id, table.property_id),
  idxProperty: index("idx_user_property_access_property").on(table.property_id),
}));

export const chat_sessions = pgTable("chat_sessions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  session_id: text("session_id").notNull().unique(),
  property_id: uuid("property_id").references(() => property.id),
  scope: text("scope", { enum: ["property", "portfolio"] }).default("portfolio").notNull(),
  guest_name: text("guest_name"),
  guest_email: text("guest_email"),
  /** Hidden from the inbox until this time; a new guest message clears it. */
  snoozed_until: timestamp("snoozed_until"),
  created_at: timestamp("created_at").defaultNow().notNull(),
  updated_at: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  idxProperty: index("idx_chat_sessions_property").on(table.property_id),
}));

export const property_content_sections = pgTable("property_content_sections", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  property_id: uuid("property_id").references(() => property.id, { onDelete: "restrict" }).notNull(),
  section_key: text("section_key").notNull(),
  content: jsonb("content").default(sql`'{}'::jsonb`).notNull(),
  sort_order: integer("sort_order").default(0).notNull(),
  is_visible: boolean("is_visible").default(true).notNull(),
  created_at: timestamp("created_at").defaultNow().notNull(),
  updated_at: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  uqSection: uniqueIndex("uq_property_content_section").on(table.property_id, table.section_key),
}));

export const property_faqs = pgTable("property_faqs", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  property_id: uuid("property_id").references(() => property.id, { onDelete: "restrict" }).notNull(),
  question: text("question").notNull(),
  answer: text("answer").notNull(),
  category: text("category"),
  sort_order: integer("sort_order").default(0).notNull(),
  is_visible: boolean("is_visible").default(true).notNull(),
  created_at: timestamp("created_at").defaultNow().notNull(),
  updated_at: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  idxProperty: index("idx_property_faqs_property").on(table.property_id, table.sort_order),
}));

export const property_seo = pgTable("property_seo", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  property_id: uuid("property_id").references(() => property.id, { onDelete: "restrict" }).notNull().unique(),
  title: text("title"),
  meta_description: text("meta_description"),
  canonical_override: text("canonical_override"),
  og_title: text("og_title"),
  og_description: text("og_description"),
  og_image: text("og_image"),
  indexable: boolean("indexable").default(true).notNull(),
  schema_overrides: jsonb("schema_overrides"),
  created_at: timestamp("created_at").defaultNow().notNull(),
  updated_at: timestamp("updated_at").defaultNow().notNull(),
});

export const property_email_template_overrides = pgTable("property_email_template_overrides", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  property_id: uuid("property_id").references(() => property.id, { onDelete: "restrict" }).notNull(),
  template_name: text("template_name").notNull(),
  subject: text("subject"),
  html_content: text("html_content"),
  text_content: text("text_content"),
  created_at: timestamp("created_at").defaultNow().notNull(),
  updated_at: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  uqOverride: uniqueIndex("uq_property_email_template").on(table.property_id, table.template_name),
}));

export const promotion_properties = pgTable("promotion_properties", {
  promotion_id: uuid("promotion_id").references(() => promotions.id, { onDelete: "cascade" }).notNull(),
  property_id: uuid("property_id").references(() => property.id, { onDelete: "restrict" }).notNull(),
}, (table) => ({
  pk: primaryKey({ columns: [table.promotion_id, table.property_id] }),
}));

/** Which homes a nearby attraction is shown for. An attraction with no links
 *  still shows for the home in its own property_id column. */
export const nearby_attraction_properties = pgTable("nearby_attraction_properties", {
  attraction_id: uuid("attraction_id").references(() => nearby_attractions.id, { onDelete: "cascade" }).notNull(),
  property_id: uuid("property_id").references(() => property.id, { onDelete: "cascade" }).notNull(),
}, (table) => ({
  pk: primaryKey({ columns: [table.attraction_id, table.property_id] }),
}));

/**
 * A change to an existing reservation's dates: an extension, a shorter stay, or
 * a move. Extra nights are priced at the stay's own nightly rate and held on
 * the calendar while the guest pays; the booking itself only changes once the
 * money is in (or at once, when nothing is owed). The row stays as the record
 * of what was asked, what it cost, and who did it.
 */
export const booking_modifications = pgTable("booking_modifications", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  booking_id: uuid("booking_id").references(() => bookings.id, { onDelete: "cascade" }).notNull(),
  property_id: uuid("property_id").references(() => property.id).notNull(),
  kind: text("kind", { enum: ["extend", "shorten", "move"] }).notNull(),
  status: text("status", { enum: ["pending_payment", "confirmed", "expired", "canceled"] }).default("pending_payment").notNull(),
  original_start_date: date("original_start_date").notNull(),
  original_end_date: date("original_end_date").notNull(),
  new_start_date: date("new_start_date").notNull(),
  new_end_date: date("new_end_date").notNull(),
  nights_delta: integer("nights_delta").notNull(),
  nightly_rate_cents: integer("nightly_rate_cents").notNull(),
  subtotal_delta_cents: integer("subtotal_delta_cents").notNull(),
  tax_delta_cents: integer("tax_delta_cents").default(0).notNull(),
  fee_delta_cents: integer("fee_delta_cents").default(0).notNull(),
  /** Positive: the guest owes this. Negative: this much is owed back, before policy. */
  total_delta_cents: integer("total_delta_cents").notNull(),
  refund_policy_pct: integer("refund_policy_pct"),
  refund_cents: integer("refund_cents").default(0).notNull(),
  currency: text("currency").default("USD").notNull(),
  payment_intent_id: text("payment_intent_id"),
  hold_ids: jsonb("hold_ids").default(sql`'[]'::jsonb`).notNull(),
  expires_at: timestamp("expires_at"),
  requested_by: uuid("requested_by").references(() => users.id),
  note: text("note"),
  requested_at: timestamp("requested_at").defaultNow().notNull(),
  paid_at: timestamp("paid_at"),
  confirmed_at: timestamp("confirmed_at"),
  closed_at: timestamp("closed_at"),
}, (table) => ({
  idx_booking: index("idx_booking_modifications_booking").on(table.booking_id),
  idx_status: index("idx_booking_modifications_status").on(table.status),
}));
export type BookingModification = typeof booking_modifications.$inferSelect;

/** Which homes a guest review is shown on. A review with no links still shows
 *  for the home in its own property_id column. */
export const guest_review_properties = pgTable("guest_review_properties", {
  review_id: uuid("review_id").references(() => guest_reviews.id, { onDelete: "cascade" }).notNull(),
  property_id: uuid("property_id").references(() => property.id, { onDelete: "cascade" }).notNull(),
}, (table) => ({
  pk: primaryKey({ columns: [table.review_id, table.property_id] }),
}));

export const coupon_properties = pgTable("coupon_properties", {
  coupon_id: uuid("coupon_id").references(() => coupons.id, { onDelete: "cascade" }).notNull(),
  property_id: uuid("property_id").references(() => property.id, { onDelete: "restrict" }).notNull(),
}, (table) => ({
  pk: primaryKey({ columns: [table.coupon_id, table.property_id] }),
}));

export const legal_document_properties = pgTable("legal_document_properties", {
  document_id: uuid("document_id").references(() => legal_documents.id, { onDelete: "cascade" }).notNull(),
  property_id: uuid("property_id").references(() => property.id, { onDelete: "restrict" }).notNull(),
}, (table) => ({
  pk: primaryKey({ columns: [table.document_id, table.property_id] }),
}));

export const property_booking_settings = pgTable("property_booking_settings", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  property_id: uuid("property_id").references(() => property.id, { onDelete: "restrict" }).notNull().unique(),
  same_day_turnover: boolean("same_day_turnover").default(false).notNull(),
  // Free days each side of a stay. Read by storage.getTurnoverDays; the number
  // itself lives in @shared/turnover so calendars and checks cannot disagree.
  buffer_nights: integer("buffer_nights").default(2).notNull(),
  payment_plan_enabled: boolean("payment_plan_enabled").default(true).notNull(),
  cancellation_policy_key: text("cancellation_policy_key").default("firm"),
  created_at: timestamp("created_at").defaultNow().notNull(),
  updated_at: timestamp("updated_at").defaultNow().notNull(),
});

export const ical_export_tokens = pgTable("ical_export_tokens", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  property_id: uuid("property_id").references(() => property.id, { onDelete: "restrict" }).notNull(),
  token: text("token").notNull().unique(),
  label: text("label"),
  revoked_at: timestamp("revoked_at"),
  created_at: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  idxProperty: index("idx_ical_export_tokens_property").on(table.property_id),
}));

/**
 * Short, shareable stand-ins for the long signed links guests are sent.
 *
 * A balance payment link was 233 characters, most of it a base64 token, which
 * wrapped over four lines in Gmail and read as spam. The code is what goes in
 * the email; the signed token it stands for never leaves the server until the
 * guest follows it.
 *
 * One live code per booking and purpose, so a resend gives the guest the same
 * link rather than a new one, and the code dies with the token behind it.
 */
export const short_links = pgTable("short_links", {
  /** Unguessable, URL-safe, and the whole point: this is what the guest sees. */
  code: text("code").primaryKey(),
  /** Path and query on this site to send them to. Never an external URL. */
  target: text("target").notNull(),
  booking_id: uuid("booking_id").references(() => bookings.id, { onDelete: "cascade" }),
  purpose: text("purpose").notNull(),
  expires_at: timestamp("expires_at"),
  created_at: timestamp("created_at").defaultNow().notNull(),
  last_used_at: timestamp("last_used_at"),
  use_count: integer("use_count").default(0).notNull(),
}, (table) => ({
  idxBookingPurpose: index("idx_short_links_booking_purpose").on(table.booking_id, table.purpose),
}));

export type ShortLink = typeof short_links.$inferSelect;

export const property_status_audit = pgTable("property_status_audit", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  property_id: uuid("property_id").references(() => property.id, { onDelete: "restrict" }).notNull(),
  actor_id: uuid("actor_id").references(() => users.id, { onDelete: "set null" }),
  field: text("field").notNull(),
  previous_value: text("previous_value"),
  new_value: text("new_value"),
  created_at: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  idxProperty: index("idx_property_status_audit_property").on(table.property_id, table.created_at),
}));

export type UserPropertyAccess = typeof user_property_access.$inferSelect;
export type ChatSession = typeof chat_sessions.$inferSelect;
export type PropertyContentSection = typeof property_content_sections.$inferSelect;
export type PropertyFaq = typeof property_faqs.$inferSelect;
export type PropertySeo = typeof property_seo.$inferSelect;
export type PropertyEmailTemplateOverride = typeof property_email_template_overrides.$inferSelect;
export type IcalExportToken = typeof ical_export_tokens.$inferSelect;
